import React, { useMemo } from 'react';
import classNames from 'classnames';
import CheckboxField from './CheckboxField';
import { RadioGroup } from 'ncoded-component-library';
import { ExtractPropTypes } from 'types';
import { Field, useFormState } from 'react-final-form';
import { RadioOption } from 'components/RadioGroupField/RadioGroupField.component';
import { useTranslation } from 'react-i18next';
import FieldWrapper from 'components/FieldWrapper';
import { FieldWrapperProps } from 'components/FieldWrapper/FieldWrapper.component';

import './CheckboxFieldGroup.styles.scss';

type RadioGroupProps = ExtractPropTypes<typeof RadioGroup>;

type CheckboxFieldGroupProps = FieldWrapperProps & {
  styled?: boolean;
} & RadioGroupProps;

const CheckboxFieldGroup: React.FC<CheckboxFieldGroupProps> = (props) => {
  const {
    className,
    options,
    name,
    direction,
    styled,
    required,
    label,
    ...rest
  } = props;
  const { t } = useTranslation();

  const classes = classNames('checkbox-field-group', className);

  const containerClasses = classNames(
    'checkbox-field-group__button-container',
    {
      'checkbox-field-group__button-container--as-row': direction === 'row',
    },
  );

  const { values, touched } = useFormState();

  const touchedFields = Object.entries(touched || {}).filter(([key, value]) => {
    if (key.includes(name)) {
      return value;
    }
    return false;
  });

  const checkboxGroupValue = useMemo(() => {
    const subObjKeys = name.split('.');
    if (!values[subObjKeys[0]]) return {};

    const nested = subObjKeys.reduce(
      (acc, key) => (acc ? acc[key as any] : {}),
      values,
    );
    return nested;
  }, [name, values]);

  const hasValue = Object.values(checkboxGroupValue || {}).some(Boolean);

  const hasError = useMemo(
    () => !hasValue && required && !!touchedFields.length,
    [hasValue, required, touchedFields.length],
  );

  const renderOptions = useMemo(
    () =>
      options?.map(({ value, label, enabled = true }: RadioOption<string>) => (
        <Field
          disabled={!enabled}
          key={value}
          name={`${name}.${value}`}
          label={typeof label === 'string' ? t(label) : label}
          styled={styled}
          type="checkbox"
          {...rest}
          component={CheckboxField}
        />
      )),
    [name, options, rest, styled, t],
  );

  return (
    <FieldWrapper
      className={classes}
      hasError={hasError}
      name={name}
      required={required}
      label={label}
      error={t('requiredField')}
    >
      <div className={containerClasses}>{renderOptions}</div>

      {!hasValue && required && (
        <Field
          name={name}
          hidden
          component="input"
          validate={() => t('requiredField')}
        />
      )}
    </FieldWrapper>
  );
};

export default CheckboxFieldGroup;
